/**
 * ReviewSubmit — publiczna strona /opinia/:token gdzie klient ocenia firmę.
 *
 * Brak autoryzacji. Token z URL → backend zwraca entity preview + status.
 * Submit: rating (1-5), title (opcjonalnie), text, allow_full_name.
 *
 * Po udanym submit:
 *   - rating ≥3 → status=published natychmiast → "Dziękujemy! Twoja opinia jest już widoczna"
 *   - rating <3 → status=submitted (czeka na moderację) → "Dziękujemy, firma została powiadomiona"
 */
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Star, CheckCircle2, Loader2, AlertTriangle, Sparkles, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { API } from '@/utils/api';

const RATING_LABELS = {
  1: 'Bardzo źle',
  2: 'Słabo',
  3: 'OK',
  4: 'Dobrze',
  5: 'Świetnie',
};

export default function ReviewSubmit() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [entity, setEntity] = useState(null);
  const [authorName, setAuthorName] = useState('');
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [allowFullName, setAllowFullName] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // null | { auto_published }

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/reviews/token/${token}`);
        setEntity(res.data.entity);
        setAuthorName(res.data.review?.author_name || '');
      } catch (e) {
        const code = e.response?.status;
        if (code === 404) setError({ kind: 'invalid', msg: 'Link jest nieprawidłowy lub wygasł.' });
        else if (code === 410) setError({ kind: 'expired', msg: 'Ta opinia została już wystawiona — dziękujemy!' });
        else setError({ kind: 'unknown', msg: 'Coś poszło nie tak. Spróbuj ponownie później.' });
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleSubmit = async () => {
    if (rating < 1) {
      toast.error('Wybierz ocenę');
      return;
    }
    if (text.trim().length < 10) {
      toast.error('Opinia musi mieć min. 10 znaków');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/reviews/submit/${token}`, {
        rating,
        title: title.trim() || null,
        text: text.trim(),
        allow_full_name: allowFullName,
      });
      setSubmitted({ auto_published: res.data?.auto_published });
    } catch (e) {
      const code = e.response?.status;
      if (code === 410) toast.error('Link wygasł');
      else if (code === 409) toast.error('Ta opinia została już wystawiona');
      else toast.error('Nie udało się wysłać opinii. Spróbuj ponownie.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-zinc-950 via-violet-950/30 to-zinc-950">
        <Loader2 className="w-8 h-8 text-violet-300 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-zinc-950 via-violet-950/30 to-zinc-950 p-6">
        <div className="max-w-md w-full bg-white/[0.03] border border-white/10 rounded-3xl p-8 text-center" data-testid="review-submit-error">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-amber-500/15 flex items-center justify-center mb-4">
            <AlertTriangle className="w-7 h-7 text-amber-400" />
          </div>
          <h1 className="text-white font-bold text-xl mb-2">
            {error.kind === 'expired' ? 'Dziękujemy!' : 'Link nieaktywny'}
          </h1>
          <p className="text-zinc-400 text-sm leading-relaxed">{error.msg}</p>
          <Link to="/" className="inline-block mt-6 px-4 py-2.5 bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold rounded-xl transition-colors">
            Wróć do APISAI
          </Link>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-zinc-950 via-emerald-950/20 to-zinc-950 p-6">
        <div className="max-w-md w-full bg-white/[0.03] border border-emerald-400/20 rounded-3xl p-8 text-center" data-testid="review-submit-success">
          <div className="w-16 h-16 mx-auto rounded-3xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mb-5 shadow-xl shadow-emerald-500/30">
            <CheckCircle2 className="w-8 h-8 text-white" strokeWidth={2.5} />
          </div>
          <h1 className="text-white font-bold text-2xl mb-2 tracking-tight">Dziękujemy!</h1>
          <p className="text-zinc-300 text-sm leading-relaxed">
            {submitted.auto_published
              ? <>Twoja zweryfikowana opinia jest już widoczna na profilu <span className="text-emerald-300 font-semibold">{entity?.name}</span>. Dzięki Tobie inni klienci podejmą lepszą decyzję. 💜</>
              : <>Twoja opinia została wysłana do <span className="text-emerald-300 font-semibold">{entity?.name}</span>. Zostanie opublikowana po weryfikacji.</>
            }
          </p>
          <div className="mt-6 flex items-center justify-center gap-2 text-emerald-300 text-xs font-semibold">
            <ShieldCheck className="w-4 h-4" /> Twoja opinia jest zweryfikowana
          </div>
          {entity?.slug && (
            <Link
              to={`/${entity.type === 'business' ? 'firma' : 'hodowla'}/${entity.slug}`}
              className="inline-block mt-6 px-5 py-2.5 bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold rounded-xl transition-colors"
            >
              Zobacz profil
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-950 via-violet-950/20 to-zinc-950 py-10 px-5" data-testid="review-submit-form">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 mb-3 shadow-lg shadow-violet-500/30">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <p className="text-violet-300 text-[10px] uppercase tracking-[0.2em] font-bold mb-1">Zweryfikowana opinia · APISAI</p>
          <h1 className="text-white text-2xl sm:text-3xl font-bold tracking-tight">
            Jak oceniasz <span className="text-fuchsia-300">{entity?.name}</span>?
          </h1>
          <p className="text-zinc-400 text-sm mt-2 leading-relaxed">
            Cześć {authorName ? authorName.split(' ')[0] : ''}, Twoja szczera opinia naprawdę pomoże innym klientom.
          </p>
        </div>

        {/* Form card */}
        <div className="bg-white/[0.03] backdrop-blur-md border border-white/10 rounded-3xl p-6 sm:p-7">
          {/* Rating */}
          <div className="mb-5 text-center">
            <Label className="text-white text-sm font-semibold block mb-3">Twoja ocena</Label>
            <div className="flex items-center justify-center gap-2" onMouseLeave={() => setHover(0)}>
              {[1, 2, 3, 4, 5].map((n) => {
                const active = (hover || rating) >= n;
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setRating(n)}
                    onMouseEnter={() => setHover(n)}
                    data-testid={`review-star-${n}`}
                    className="p-1 transition-transform hover:scale-110"
                    aria-label={`${n} gwiazdek`}
                  >
                    <Star
                      className={`w-9 h-9 sm:w-10 sm:h-10 transition-colors ${
                        active ? 'fill-amber-400 text-amber-400' : 'text-zinc-700'
                      }`}
                      strokeWidth={1.5}
                    />
                  </button>
                );
              })}
            </div>
            {(hover || rating) > 0 && (
              <p className="text-amber-300 text-sm font-semibold mt-2" data-testid="rating-label">
                {RATING_LABELS[hover || rating]}
              </p>
            )}
          </div>

          {/* Title */}
          <div className="mb-4">
            <Label className="text-zinc-300 text-xs font-medium mb-1.5 block">Tytuł opinii (opcjonalnie)</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="np. Polecam, profesjonalna obsługa"
              maxLength={120}
              data-testid="review-title-input"
              className="bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600"
            />
          </div>

          {/* Text */}
          <div className="mb-4">
            <Label className="text-zinc-300 text-xs font-medium mb-1.5 block">Opisz swoje doświadczenie *</Label>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Jak przebiegał kontakt? Co Ci się podobało? Co byś zmienił?"
              rows={5}
              maxLength={1500}
              data-testid="review-text-input"
              className="bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600"
            />
            <p className="text-zinc-500 text-[10px] mt-1 text-right">{text.length}/1500</p>
          </div>

          {/* Privacy */}
          <label className="flex items-start gap-2.5 mb-5 cursor-pointer">
            <Checkbox
              checked={allowFullName}
              onCheckedChange={(v) => setAllowFullName(!!v)}
              data-testid="review-fullname-checkbox"
              className="mt-0.5 data-[state=checked]:bg-violet-600 data-[state=checked]:border-violet-600 border-white/20"
            />
            <span className="text-zinc-400 text-xs leading-relaxed">
              Pokaż moje pełne imię i nazwisko ({authorName || 'Klient'}). Domyślnie pokażemy tylko imię i pierwszą literę nazwiska.
            </span>
          </label>

          <Button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || rating < 1 || text.trim().length < 10}
            data-testid="review-submit-btn"
            className="w-full bg-gradient-to-br from-violet-500 to-fuchsia-500 hover:opacity-90 text-white font-bold h-12 rounded-xl text-base shadow-xl shadow-violet-500/30"
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
            {submitting ? 'Wysyłam…' : 'Wystaw opinię'}
          </Button>

          <p className="text-zinc-500 text-[10px] text-center mt-3 flex items-center justify-center gap-1.5">
            <ShieldCheck className="w-3 h-3 text-emerald-400" />
            Opinia oznaczona jako zweryfikowana — tylko klienci, którzy pisali przez APISAI mogą ją wystawić
          </p>
        </div>

        <p className="text-zinc-600 text-[10px] text-center mt-5">
          APISAI · Visibility Operating System
        </p>
      </div>
    </div>
  );
}
